import { FC, MouseEvent, useEffect } from "react";

import { useCanDeleteActions } from "../../../utils/delete";

import { TopicNavigationButtons } from "./TopicNavigationButtons";

import { HoverableEditableText } from "components/hover-widgets/EditableText";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { TopicSidebarOptionsMenu } from "components/menus/TopicSidebarOptionsMenu";
import type { TopicType } from "components/server-types";
import { t } from "i18n/i18n";
import { Listbox, type ListboxOption } from "mds/components/Listbox";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { PageListIcon, PageListStatusIcon, TrashDeleteIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  type TopicWithPagesType,
  selectCurrentSectionId,
  selectCurrentSectionIsLinked,
  selectCurrentSectionIsTemplate,
  selectVisibleTopicsWithPages,
} from "store/selectors";
import { useFullListSelector } from "store/store-hooks";
import { changeReleaseStatusForPages } from "utils/page";
import { useNavigate } from "utils/routing";
import { useTopicActions } from "utils/topic";
import { pageUrl } from "utils/urls";

export type TopicSidebarPanelHeaderProps = {
  topic: Pick<TopicType, "id" | "title" | "released_page_count" | "unreleased_page_count">;
  onDelete: () => void;
};

// This panel displays each type of activity and its child pages for a given topic.
// It is also for selecting the individual page/worksheet to be displayed in main page.
export const TopicSidebarPanelHeader: FC<TopicSidebarPanelHeaderProps> = ({ topic, onDelete }) => {
  const { canAuthorSection } = useCapabilities();
  const currentSectionId = useAppSelector(selectCurrentSectionId);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isInLinkedSection = useAppSelector(selectCurrentSectionIsLinked);
  // Nested topic/page tree used for nav and release actions.
  const visibleTopics = useFullListSelector(selectVisibleTopicsWithPages);
  const currentTopic = visibleTopics.find((visibleTopic) => visibleTopic.id === topic.id)!;
  const navigate = useNavigate();

  const topicItems = visibleTopics.map((topicItem) => ({
    id: topicItem.id,
    title: topicItem.title,
  }));

  const isSmallScreen = !useIsMdOrLarger();
  const { canDeleteTopic } = useCanDeleteActions(topic.id);

  const { saveTopic } = useTopicActions();

  useEffect(() => {
    if (currentSectionId) {
      storeApi.topics.list({ section_id: currentSectionId });
      // TODO: The topic sidebar dropdown won't work correctly for students when they load a page
      // directly, because we haven't yet loaded any activities/pages for the topics, which the
      // store selector relies on to calculate visible topics. Loading all pages/activities is not
      // an adequate solution for that, and we should find a better way to handle this.
      storeApi.pages.list({ section_id: currentSectionId });
      storeApi.activities.list({ section_id: currentSectionId });
    }
  }, [currentSectionId]);

  const onTopicTitleChanged = (newTitle: string) => {
    saveTopic({ id: topic.id, title: newTitle });
  };

  const onTopicChange = (item: ListboxOption) => {
    if (item.id) {
      onNavigateToTopic(item.id);
    }
  };

  const onNavigateToTopic = (topicId: string) => {
    if (!currentSectionId) return;
    navigate(pageUrl(currentSectionId, "", { topicId }));
  };

  const shouldReleasePages = (currentTopic?.unreleased_page_count ?? 0) > 0;

  const extraListItems = getTopicSidebarHeaderActions({
    canDeleteTopic,
    currentTopic,
    isInLinkedSection,
    isInTemplateSection,
    shouldReleasePages,
    topic,
    onDelete,
  });

  return (
    <div className="mt-[1px]">
      <div className="flex items-center justify-between">
        <Listbox
          buttonKind="ghost"
          buttonSize="xs"
          buttonText={t("glossary.topic")}
          items={topicItems}
          openMenuEventName="Sidebar - Content / Topic dropdown"
          selectedValue={topic?.id}
          onSelectionChange={(item) => onTopicChange(item)}
        />

        <TopicNavigationButtons
          currentTopicId={topic.id}
          topics={visibleTopics}
          onChange={onNavigateToTopic}
        />
      </div>

      <HoverableToolbar
        contentClassName="mt-0.5 bg-black-tint-95"
        disableUnlessEditMode={!isInLinkedSection}
        extraListItems={extraListItems}
        readOnly={!canAuthorSection}
        reference="topic"
        showEditButton={!isInLinkedSection}
        uncentered
      >
        <h3 className="h3 my-0 ml-2 flex w-full items-center gap-2 text-black-tint-20">
          {isSmallScreen && <PageListIcon className="text-blue" />}
          <HoverableEditableText
            label={t("glossary.topic_title")}
            textSize="h3"
            value={topic.title}
            disableUnlessEditMode
            shouldWrap
            onValueChanged={onTopicTitleChanged}
          />
        </h3>
      </HoverableToolbar>
    </div>
  );
};

const getTopicSidebarHeaderActions = ({
  canDeleteTopic,
  currentTopic,
  isInLinkedSection,
  isInTemplateSection,
  onDelete,
  shouldReleasePages,
  topic,
}: {
  canDeleteTopic: boolean;
  currentTopic: TopicWithPagesType;
  isInLinkedSection: boolean;
  isInTemplateSection: boolean;
  onDelete: () => void;
  shouldReleasePages: boolean;
  topic: Pick<TopicType, "released_page_count" | "unreleased_page_count">;
}) => {
  if (isInTemplateSection) {
    return (
      <li>
        <SquareIconButton
          aria-disabled={!canDeleteTopic}
          aria-label={
            canDeleteTopic ? t("common.delete") : t("tooltip.delete_topic_has_assessments")
          }
          kind="ghost"
          size="s"
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete();
          }}
        >
          <TrashDeleteIcon />
        </SquareIconButton>
      </li>
    );
  }

  if (isInLinkedSection) {
    return (
      <li>
        <SquareIconButton
          aria-label={t(
            shouldReleasePages
              ? "sidebar_options_menu.tooltip.release_pages_topic"
              : "sidebar_options_menu.tooltip.unrelease_pages_topic",
          )}
          kind="ghost"
          size="s"
          onClick={(e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            changeReleaseStatusForPages(currentTopic?.pages || []);
          }}
        >
          <PageListStatusIcon statusColor={shouldReleasePages ? "green" : "gray"} />
        </SquareIconButton>
      </li>
    );
  }

  return (
    <li>
      <TopicSidebarOptionsMenu
        isInTemplateSection={isInTemplateSection}
        optionsMenuActions={{
          onDelete,
          isDeleteDisabled: !canDeleteTopic,
          deleteTooltipText: canDeleteTopic ? undefined : t("tooltip.delete_topic_has_assessments"),
          onChangeStatus: () => changeReleaseStatusForPages(currentTopic?.pages || []),
        }}
        releasedCount={topic.released_page_count}
        unreleasedCount={topic.unreleased_page_count}
      />
    </li>
  );
};
